import { Street } from '../pages/PlaygroundPage/PlaygroundPage';

export interface HandSummaryStreetAction {
  playerId: number;
  type: string;
  amount: number | null;
}

export interface HandSummaryStreet {
  potSizeAtStart: number;
  potSizeAtEnd: number;
  cardsDealt: string[] | null;
  actions: HandSummaryStreetAction[];
}

export interface Player {
  id: number;
  name: string;
  isHero: boolean;
  position: string;
  initialStack: number;
}

/**
 * Calculates the pot size up to a specific street and action index
 */
export const calculatePotSize = (
  handData: any,
  targetStreet: Street,
  targetActionIndex: number,
  streetsOrder: Street[]
): number => {
  let potSize = handData.preflop.potSizeAtStart;
  
  for (const street of streetsOrder) {
    const streetData = handData[street];
    if (!streetData) continue;
    
    const streetIndex = streetsOrder.indexOf(street);
    const targetStreetIndex = streetsOrder.indexOf(targetStreet);
    
    if (streetIndex < targetStreetIndex) {
      // Include all actions from completed streets
      for (const action of streetData.actions) {
        potSize += action.amount || 0;
      }
    } else if (streetIndex === targetStreetIndex) {
      // Include actions up to target index
      for (let i = 0; i <= targetActionIndex && i < streetData.actions.length; i++) {
        const action = streetData.actions[i];
        potSize += action.amount || 0;
      }
      break;
    }
  }
  
  return potSize;
};

/**
 * Calculates updated stack sizes after actions up to a specific point
 */
export const calculateStackSizes = (
  initialStacks: Record<number, number>,
  handData: any,
  targetStreet: Street,
  targetActionIndex: number,
  streetsOrder: Street[],
  blinds: { smallBlind: number; bigBlind: number },
  players: Player[]
): Record<number, number> => {
  const newStackSizes = { ...initialStacks };
  
  // Apply blinds for preflop
  const sbPlayer = players.find(p => p.position === 'SB');
  const bbPlayer = players.find(p => p.position === 'BB');
  
  if (sbPlayer) newStackSizes[sbPlayer.id] -= blinds.smallBlind;
  if (bbPlayer) newStackSizes[bbPlayer.id] -= blinds.bigBlind;
  
  // Apply action amounts
  for (const street of streetsOrder) {
    const streetData = handData[street];
    if (!streetData) continue;
    
    const streetIndex = streetsOrder.indexOf(street);
    const targetStreetIndex = streetsOrder.indexOf(targetStreet);
    
    if (streetIndex < targetStreetIndex) {
      // Include all actions from completed streets
      for (const action of streetData.actions) {
        if (action.amount && action.amount > 0) {
          newStackSizes[action.playerId] -= action.amount;
        }
      }
    } else if (streetIndex === targetStreetIndex) {
      // Include actions up to target index
      for (let i = 0; i <= targetActionIndex && i < streetData.actions.length; i++) {
        const action = streetData.actions[i];
        if (action.amount && action.amount > 0) {
          newStackSizes[action.playerId] -= action.amount;
        }
      }
      break;
    }
  }
  
  return newStackSizes;
};

/**
 * Calculates player contributions for the current street
 */
export const calculateStreetContributions = (
  handData: any,
  street: Street,
  actionIndex: number,
  players: Player[],
  blinds: { smallBlind: number; bigBlind: number }
): Record<number, number> => {
  const contributions: Record<number, number> = {};
  
  // Initialize all players to 0
  players.forEach(p => {
    contributions[p.id] = 0;
  });
  
  // Add blinds for preflop
  if (street === 'preflop') {
    const sbPlayer = players.find(p => p.position === 'SB');
    const bbPlayer = players.find(p => p.position === 'BB');
    
    if (sbPlayer) contributions[sbPlayer.id] = blinds.smallBlind;
    if (bbPlayer) contributions[bbPlayer.id] = blinds.bigBlind;
  }
  
  // Add action contributions
  const streetData = handData[street];
  if (streetData) {
    for (let i = 0; i <= actionIndex && i < streetData.actions.length; i++) {
      const action = streetData.actions[i];
      const currentContribution = contributions[action.playerId] || 0;
      contributions[action.playerId] = currentContribution + (action.amount || 0);
    }
  }
  
  return contributions;
};

/**
 * Determines which players have folded up to a specific point
 */
export const calculateFoldedPlayers = (
  handData: any,
  targetStreet: Street,
  targetActionIndex: number,
  streetsOrder: Street[]
): Set<number> => {
  const foldedPlayers = new Set<number>();
  
  for (const street of streetsOrder) {
    const streetData = handData[street];
    if (!streetData) continue;
    
    const streetIndex = streetsOrder.indexOf(street);
    const targetStreetIndex = streetsOrder.indexOf(targetStreet);
    
    if (streetIndex < targetStreetIndex) {
      // Include all actions from completed streets
      for (const action of streetData.actions) {
        if (action.type === 'fold') {
          foldedPlayers.add(action.playerId);
        }
      }
    } else if (streetIndex === targetStreetIndex) {
      // Include actions up to target index
      for (let i = 0; i <= targetActionIndex && i < streetData.actions.length; i++) {
        const action = streetData.actions[i];
        if (action.type === 'fold') {
          foldedPlayers.add(action.playerId);
        }
      }
      break;
    }
  }
  
  return foldedPlayers;
};

/**
 * Gets community cards that should be displayed up to a specific street
 */
export const getCommunityCards = (
  handData: any,
  currentStreet: Street,
  streetsOrder: Street[]
): string[] => {
  if (!handData.board) return [];
  
  const currentStreetIndex = streetsOrder.indexOf(currentStreet);
  
  if (currentStreetIndex >= streetsOrder.indexOf('flop')) {
    if (currentStreetIndex >= streetsOrder.indexOf('river')) {
      return handData.board.slice(0, 5); // All 5 cards
    } else if (currentStreetIndex >= streetsOrder.indexOf('turn')) {
      return handData.board.slice(0, 4); // Flop + turn
    } else {
      return handData.board.slice(0, 3); // Just flop
    }
  }
  
  return []; // Preflop - no community cards
};

/**
 * Calculates the current bet level for a street
 */
export const calculateCurrentBet = (
  contributions: Record<number, number>,
  bigBlind: number
): number => {
  const maxContribution = Math.max(...Object.values(contributions), bigBlind);
  return maxContribution;
};

/**
 * Validates and caps a bet amount based on player's remaining stack
 */
export const capBetAmount = (
  desiredAmount: number,
  currentContribution: number,
  remainingStack: number
): { finalAmount: number; additionalAmount: number } => {
  const additionalNeeded = desiredAmount - currentContribution;
  const actualAdditional = Math.min(additionalNeeded, remainingStack);
  const finalAmount = currentContribution + actualAdditional;
  
  return {
    finalAmount,
    additionalAmount: actualAdditional
  };
};